import React, { useState, useEffect, useMemo } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import api from '../services/api';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import './AdminScreen.css';

const AdminScreen = () => {
  const { t } = useLocalization();
  const [activeTab, setActiveTab] = useState('activity');
  const [loading, setLoading] = useState(false);
  const [activityStats, setActivityStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [consistencyData, setConsistencyData] = useState(null);
  const [consistencyLoading, setConsistencyLoading] = useState(false);
  
  // TanStack Table state
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: 'attempted_at', desc: true }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });

  const tabs = [
    { key: 'activity', label: t('admin.activity'), icon: '📈' },
    { key: 'consistency', label: t('admin.consistencyCheck'), icon: '🔍' },
  ];

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchActivityData();
      fetchRecentActivity();
    } else if (activeTab === 'consistency') {
      fetchConsistencyData();
    }
  }, [activeTab]);

  const fetchActivityData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/activity/stats');
      setActivityStats(response.data);
    } catch (error) {
      console.error('Failed to fetch activity stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await api.get('/api/admin/activity/recent', {
        params: { per_page: 1000 } // Get more data for client-side filtering
      });
      setRecentActivity(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
    }
  };

  const fetchConsistencyData = async () => {
    try {
      setConsistencyLoading(true);
      const response = await api.get('/api/admin/consistency/check');
      setConsistencyData(response.data);
    } catch (error) {
      console.error('Failed to fetch consistency data:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setConsistencyLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const time = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      second: '2-digit', 
      hour12: true 
    });
    return `${year}-${month}-${day} ${time}`;
  };

  const formatTimeSpent = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  const getActivityLevel = (activity) => {
    if (activity === 0) return 'none';
    if (activity <= 3) return 'low';
    if (activity <= 7) return 'medium';
    if (activity <= 12) return 'high';
    return 'very-high';
  };

  const getHeatmapCellStyle = (activity) => {
    const level = getActivityLevel(activity);
    const styles = {
      'none': 'bg-gray-100',
      'low': 'bg-green-200',
      'medium': 'bg-green-400',
      'high': 'bg-green-600',
      'very-high': 'bg-green-800'
    };
    return styles[level] || 'bg-gray-100';
  };

  // Define table columns
  const columns = useMemo(() => [
    {
      accessorKey: 'attempted_at',
      header: t('admin.time'),
      cell: info => formatDateTime(info.getValue()),
      filterFn: 'includesString',
    },
    {
      accessorKey: 'player_name',
      header: t('admin.playerName'),
      filterFn: 'includesString',
    },
    {
      accessorKey: 'module_id',
      header: t('admin.moduleId'),
      filterFn: 'includesString',
    },
    {
      accessorKey: 'card_id',
      header: t('admin.cardId'),
      filterFn: 'includesString',
    },
    {
      accessorKey: 'is_correct',
      header: t('admin.isCorrect'),
      cell: info => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          info.getValue() 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {info.getValue() ? t('admin.correct') : t('admin.incorrect')}
        </span>
      ),
      filterFn: (row, columnId, value) => {
        if (!value) return true;
        if (value === 'true') return row.getValue(columnId) === true;
        if (value === 'false') return row.getValue(columnId) === false;
        return true;
      },
    },
    {
      accessorKey: 'time_spent',
      header: t('admin.timeSpent'),
      cell: info => formatTimeSpent(info.getValue()),
      filterFn: 'includesString',
    },
  ], [t]);

  const table = useReactTable({
    data: recentActivity,
    columns,
    state: {
      columnFilters,
      sorting,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const getTabStyles = (tab) => {
    const isActive = activeTab === tab.key;
    return `inline-flex items-center space-x-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${
      isActive 
        ? 'border-blue-500 text-blue-600' 
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;
  };

  const renderHeatmap = () => {
    if (!activityStats?.heatmap) return null;

    // Create a map for quick lookup
    const activityMap = {};
    activityStats.heatmap.forEach(item => {
      activityMap[item.date] = item.activity_count;
    });

    // Generate last 90 days
    const days = [];
    const today = new Date();
    for (let i = 89; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      days.push({
        date: dateStr,
        activity: activityMap[dateStr] || 0
      });
    }

    // Group into weeks
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-800">{t('admin.allPlayersHeatmap')}</h4>
        <div className="space-y-2">
          <div className="w-full overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="flex flex-col gap-1">
                  {week.map((day, dayIndex) => (
                    <div
                      key={dayIndex}
                      className={`w-2 h-2 rounded-sm ${getHeatmapCellStyle(day.activity)} cursor-pointer transition-transform hover:scale-125`}
                      title={`${new Date(day.date).toLocaleDateString()}: ${day.activity} activities`}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-center text-xs text-gray-500">
            <span className="mr-2">Less</span>
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-100 rounded-sm"></div>
              <div className="w-2 h-2 bg-green-200 rounded-sm"></div>
              <div className="w-2 h-2 bg-green-400 rounded-sm"></div>
              <div className="w-2 h-2 bg-green-600 rounded-sm"></div>
              <div className="w-2 h-2 bg-green-800 rounded-sm"></div>
            </div>
            <span className="ml-2">More</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSolvedTasksStats = () => {
    if (!activityStats?.players) return null;

    const totalSolvedTasks = activityStats.players.reduce((sum, player) => sum + player.solved_tasks, 0);
    const totalAttempts = activityStats.players.reduce((sum, player) => sum + player.total_attempts, 0);

    return (
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-800">{t('admin.allPlayersSolved')}</h4>
        
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{totalSolvedTasks}</div>
            <div className="text-sm font-medium text-gray-700">Total Solved</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{totalAttempts}</div>
            <div className="text-sm font-medium text-gray-700">Total Attempts</div>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{activityStats.players.length}</div>
            <div className="text-sm font-medium text-gray-700">Total Players</div>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">
              {totalAttempts > 0 ? Math.round((totalSolvedTasks / totalAttempts) * 100) : 0}%
            </div>
            <div className="text-sm font-medium text-gray-700">Success Rate</div>
          </div>
        </div>

        {/* Player List */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Player
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Solved Tasks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Attempts
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Success Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activityStats.players.map((player) => (
                  <tr key={player.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {player.first_name} {player.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.solved_tasks}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.total_attempts}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.total_attempts > 0 
                        ? Math.round((player.solved_tasks / player.total_attempts) * 100) + '%'
                        : '0%'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderActivityTable = () => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h4 className="text-lg font-semibold text-gray-800">{t('admin.recentActivity')}</h4>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button
              onClick={() => {
                setColumnFilters([]);
                table.resetSorting();
              }}
              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs transition-colors"
            >
              {t('admin.clearFilters')}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {table.getHeaderGroups()[0]?.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center space-x-1">
                        <span>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        <span>
                          {{
                            asc: '↑',
                            desc: '↓',
                          }[header.column.getIsSorted()] ?? null}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
                <tr>
                  {table.getHeaderGroups()[0]?.headers.map((header) => (
                    <th key={header.id} className="px-6 py-2">
                      {header.column.getCanFilter() ? (
                        header.column.id === 'is_correct' ? (
                          <select
                            value={header.column.getFilterValue() ?? ''}
                            onChange={(e) => header.column.setFilterValue(e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">{t('admin.allResults')}</option>
                            <option value="true">{t('admin.correct')}</option>
                            <option value="false">{t('admin.incorrect')}</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={header.column.getFilterValue() ?? ''}
                            onChange={(e) => header.column.setFilterValue(e.target.value)}
                            placeholder={`Filter ${header.column.columnDef.header}...`}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        )
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      {t('admin.noActivities')}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {table.getPageCount() > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            <div className="flex justify-between sm:hidden">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {t('admin.previous')}
              </button>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {t('admin.next')}
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )}{' '}
                  of {table.getFilteredRowModel().rows.length} results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('admin.previous')}
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, table.getPageCount()) }, (_, i) => {
                    const page = i + Math.max(0, table.getState().pagination.pageIndex - 2);
                    if (page >= table.getPageCount()) return null;
                    return (
                      <button
                        key={page}
                        onClick={() => table.setPageIndex(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          table.getState().pagination.pageIndex === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page + 1}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('admin.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderActivity = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
          <span className="ml-2 text-gray-600">{t('common.loading')}</span>
        </div>
      );
    }

    return (
      <div className="admin-content">
        <div className="space-y-8">
          {renderSolvedTasksStats()}
          {renderHeatmap()}
          {renderActivityTable()}
        </div>
      </div>
    );
  };

  const renderConsistencyCheck = () => {
    if (consistencyLoading) {
      return (
        <div className="admin-content">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
            <span className="ml-2 text-gray-600">{t('common.loading')}</span>
          </div>
        </div>
      );
    }

    if (!consistencyData) {
      return (
        <div className="admin-content">
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">No consistency check has been run yet.</p>
            <button
              onClick={fetchConsistencyData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Run Consistency Check
            </button>
          </div>
        </div>
      );
    }

    // Backend returns: { categories, checks, execution_time_ms, severities, timestamp, total_checks, total_issues }
    const { 
      checks = [], 
      timestamp, 
      total_checks = 0, 
      total_issues = 0, 
      categories = {},
      severities = {} 
    } = consistencyData || {};
    
    // Create summary object from top-level properties
    const summary = {
      timestamp,
      total_checks,
      total_issues,
      critical_issues: severities.critical || 0
    };
    
    // Backend returns checks with affected_entities, not separate issues
    // Convert each check into individual issues for each affected entity
    const issues = checks.flatMap(check => 
      (check.affected_entities || []).map(entity => ({
        check_name: check.check_name,
        category: check.category,
        severity: check.severity,
        description: check.description,
        affected_entity: entity
      }))
    );
    
    console.log('Processed data:');
    console.log('- total checks from backend:', checks.length);
    console.log('- extracted individual issues:', issues.length);
    console.log('- issues by category:', {
      database: issues.filter(i => i.category === 'database').length,
      content: issues.filter(i => i.category === 'content').length,
      localization: issues.filter(i => i.category === 'localization').length
    });
    
    const categorizedIssues = {
      database: issues.filter(issue => issue.category === 'database'),
      content: issues.filter(issue => issue.category === 'content'),
      localization: issues.filter(issue => issue.category === 'localization')
    };

    const getSeverityColor = (severity) => {
      switch (severity) {
        case 'critical': return 'bg-red-100 text-red-800 border-red-200';
        case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
        default: return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    };

    const getSeverityIcon = (severity) => {
      switch (severity) {
        case 'critical': return '❌';
        case 'warning': return '⚠️';
        case 'info': return 'ℹ️';
        default: return '•';
      }
    };

    return (
      <div className="admin-content">
        <div className="space-y-6">
          {/* Header with refresh button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">{t('admin.consistencyCheck')}</h3>
              <p className="text-sm text-gray-600 mt-1">
                Last checked: {summary.timestamp ? new Date(summary.timestamp).toLocaleString() : 'Never'}
              </p>
            </div>
            <button
              onClick={fetchConsistencyData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              disabled={consistencyLoading}
            >
              {consistencyLoading ? 'Checking...' : 'Refresh Check'}
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="text-2xl font-bold text-green-600">{summary.total_checks || 0}</div>
              <div className="text-sm font-medium text-gray-700">Total Checks</div>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="text-2xl font-bold text-red-600">{summary.total_issues || 0}</div>
              <div className="text-sm font-medium text-gray-700">Total Issues</div>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="text-2xl font-bold text-yellow-600">{summary.critical_issues || 0}</div>
              <div className="text-sm font-medium text-gray-700">Critical Issues</div>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="text-2xl font-bold text-blue-600">
                {(summary.total_checks || 0) - (summary.total_issues || 0)}
              </div>
              <div className="text-sm font-medium text-gray-700">Passed Checks</div>
            </div>
          </div>

          {/* Status Message */}
          {(summary.total_issues || 0) === 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <span className="text-green-500 text-xl mr-2">✅</span>
                <span className="text-green-800 font-medium">All consistency checks passed!</span>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <span className="text-red-500 text-xl mr-2">⚠️</span>
                <span className="text-red-800 font-medium">
                  Found {summary.total_issues || 0} issue{(summary.total_issues || 0) !== 1 ? 's' : ''} that need attention
                </span>
              </div>
            </div>
          )}

          {/* Issues by Category */}
          {Object.entries(categorizedIssues).map(([category, categoryIssues]) => {
            if (categoryIssues.length === 0) return null;

            return (
              <div key={category} className="bg-white rounded-lg border shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-800 capitalize">
                    {category} Issues ({categoryIssues.length})
                  </h4>
                </div>
                <div className="divide-y divide-gray-200">
                  {categoryIssues.map((issue, index) => (
                    <div key={index} className="px-6 py-4">
                      <div className="flex items-start space-x-3">
                        <span className="text-lg flex-shrink-0 mt-0.5">
                          {getSeverityIcon(issue.severity)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-medium text-gray-900">{issue.check_name}</span>
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${getSeverityColor(issue.severity)}`}>
                              {issue.severity}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{issue.description}</p>
                          {issue.affected_entity && Object.keys(issue.affected_entity).length > 0 && (
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-xs font-medium text-gray-700 mb-1">Affected Entity:</div>
                              <pre className="text-xs text-gray-600 overflow-x-auto">
                                {JSON.stringify(issue.affected_entity, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Empty state for when there are no issues */}
          {(summary.total_issues || 0) === 0 && (
            <div className="text-center py-8">
              <span className="text-6xl">🎉</span>
              <p className="text-lg font-medium text-gray-800 mt-4">System is healthy!</p>
              <p className="text-gray-600">All {summary.total_checks || 0} consistency checks passed successfully.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-screen">
      <div className="admin-header">
        <h1 className="admin-title">{t('navigation.adminArea')}</h1>
      </div>

      <main className="admin-main">
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <nav className="flex space-x-8 border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={getTabStyles(tab)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'activity' && renderActivity()}
          {activeTab === 'consistency' && renderConsistencyCheck()}
        </div>
      </main>
    </div>
  );
};

export default AdminScreen;